import { notify } from '@billy/design-system'
import { useMemo } from 'react'
import { useQuery } from 'react-query'

import { defaultStaleTime } from '../../../config/queryClient'
import { NotificationKeys } from '../../../enums/notificationKeys'
import { QueryKeys } from '../../../enums/queryKeys'
import { useSegment } from '../../../hooks'
import { defaultProductsFilters } from '../constants/defaultProductsFilters'
import { ProductLoadErrorMessage } from '../elements/ProductLoadErrorMessage'
import { fetchProducts } from '../query-api'
import { Product } from '../types/product'
import { ProductsFilters } from '../types/productsFilters'

interface UseFetchProductsResponse {
  isError: boolean
  isLoading: boolean
  products: Product[]
  totalItems: number
}

interface UseFetchProductsOptions {
  keepPreviousData?: boolean
}

export const useFetchProducts = (
  filters?: ProductsFilters,
  { keepPreviousData = false }: UseFetchProductsOptions = {},
): UseFetchProductsResponse => {
  const { track } = useSegment()

  const filtersWithDefaults: ProductsFilters = useMemo(() => ({ ...defaultProductsFilters, ...filters }), [filters])

  const queryKey = useMemo(() => [QueryKeys.Products, filtersWithDefaults], [filtersWithDefaults])

  const {
    data: productsResponse,
    isIdle,
    isLoading,
    isError,
  } = useQuery(queryKey, () => fetchProducts(filtersWithDefaults), {
    keepPreviousData,
    staleTime: defaultStaleTime,
    onError: () => {
      notify({
        id: NotificationKeys.ProductsList,
        message: <ProductLoadErrorMessage />,
        variant: 'error',
      })
      track('xxx - invoicing success - item - listing - loading error occurred')
    },
  })

  const totalCount = Number(productsResponse?.headers['x-total-count'])

  return {
    isError,
    isLoading: isIdle || isLoading,
    products: productsResponse?.data || [],
    totalItems: Number.isInteger(totalCount) ? totalCount : 0,
  }
}
